<template>
  <div
    class="ws-theme-root"
    :class="{ 'ws-theme-root--storefront': isStorefront, 'ws-theme-root--desktop': desktop, 'ws-theme-root--desktop-auth': desktop && isAuth }"
    :data-theme-mode="isStorefront ? 'light' : workspaceTheme.appearance.mode"
    :data-theme-elevation="isStorefront ? 'soft' : workspaceTheme.appearance.elevation"
  >
    <ThemeStyleSheet v-if="!isStorefront" :source="styleSheet" />
    <ThemeStage v-if="!isStorefront" />
    <div class="theme-app">
      <a-config-provider :locale="zhCN" :theme="theme">
        <DesktopWindowBar />
        <DesktopUpdatePanel v-if="desktop" compact />
        <MobileUpdatePanel v-if="mobileUpdates.available" compact />
        <ServiceStatusBanner :session-error="auth.initializationError" @retry-session="retrySession" />
        <RouterView v-if="!auth.initializationError" />
        <CreditPurchaseDrawer />
        <AdvertisingHost />
        <CreditRewardAnimation />
      </a-config-provider>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, watch } from 'vue';
import { RouterView, useRoute } from 'vue-router';
import zhCN from 'ant-design-vue/es/locale/zh_CN';
import { theme as antTheme } from 'ant-design-vue';
import CreditPurchaseDrawer from '@/components/commerce/CreditPurchaseDrawer.vue';
import ServiceStatusBanner from '@/components/system/ServiceStatusBanner.vue';
import AdvertisingHost from '@/components/advertising/AdvertisingHost.vue';
import CreditRewardAnimation from '@/components/advertising/CreditRewardAnimation.vue';
import ThemeStage from '@/components/theme/ThemeStage.vue';
import ThemeStyleSheet from '@/components/theme/ThemeStyleSheet.vue';
import DesktopWindowBar from '@/components/system/DesktopWindowBar.vue';
import DesktopUpdatePanel from '@/components/system/DesktopUpdatePanel.vue';
import MobileUpdatePanel from '@/components/system/MobileUpdatePanel.vue';
import { useMobileUpdateStore } from '@/stores/mobile-update.store';
import { useDesktopStore } from '@/stores/desktop.store';
import { getDesktopBridge } from '@/desktop';
import { useAuthStore } from '@/stores/auth.store';
import { useCreditPurchaseStore } from '@/stores/credit-purchase.store';
import { useWorkspaceThemeStore } from '@/stores/workspace-theme.store';
import { useServiceStatusStore } from '@/stores/service-status.store';
import { useWorkspaceSidebarCollapse } from '@/composables/useWorkspaceSidebarCollapse';
import { useClientNavigationContext } from '@/composables/useClientNavigationContext';
import { clientNavigationContext } from '@/utils/client-navigation';
import { useCompactViewport } from '@/composables/useCompactViewport';
const auth = useAuthStore();
function retrySession(): void { window.location.reload(); }
const purchase = useCreditPurchaseStore();
const workspaceTheme = useWorkspaceThemeStore();
const route = useRoute();
const { workspaceId: currentWorkspaceId } = useWorkspaceSidebarCollapse();
useClientNavigationContext(() => clientNavigationContext(route, currentWorkspaceId.value));
const desktop = getDesktopBridge();
useDesktopStore().startUpdates();
useDesktopStore().startBrowser();
const mobileUpdates = useMobileUpdateStore();
const startMobileUpdates = () => { void mobileUpdates.start(); };
window.addEventListener('client:content-ready', startMobileUpdates);
watch(() => [route.meta.standaloneComponent, auth.initializationError], () => {
  if (!route.meta.standaloneComponent || auth.initializationError) startMobileUpdates();
}, { immediate: true });
useServiceStatusStore().start();
function openPurchase(_event: Event): void {
  purchase.show();
}
onMounted(() => window.addEventListener('ai-credits-insufficient', openPurchase));
onBeforeUnmount(() => {
  window.removeEventListener('ai-credits-insufficient', openPurchase);
  window.removeEventListener('client:content-ready', startMobileUpdates);
});
const isStorefront = computed(() => route.meta.storefront === true);
const isAuth = computed(() => route.path.startsWith('/auth'));
const compactViewport = useCompactViewport();
const storefrontTheme = {
  algorithm: antTheme.defaultAlgorithm,
  token: {
    colorPrimary: '#6857ed',
    colorLink: '#6857ed',
    colorBgBase: '#f6f7fb',
    colorBgContainer: '#ffffff',
    colorBgElevated: '#ffffff',
    colorText: '#172c4b',
    colorTextSecondary: '#647590',
    colorBorder: '#e5eaf3',
    colorBorderSecondary: '#e5eaf3',
    borderRadius: 6,
    controlOutlineWidth: 2,
    controlHeight: 34,
    controlHeightSM: 28,
    controlHeightLG: 40,
    fontSize: 12,
    fontFamily: "'Microsoft YaHei UI', 'Microsoft YaHei', 'PingFang SC', system-ui, sans-serif",
  },
};
const theme = computed(() => {
  const base = isStorefront.value ? storefrontTheme : workspaceTheme.antTheme;
  return compactViewport.value
    ? { ...base, token: { ...base.token, controlHeight: 44, controlHeightSM: 44, controlHeightLG: 48, fontSize: 14 } }
    : base;
});
const styleSheet = computed(() => workspaceTheme.runtime?.styleSheet ?? '');
</script>

<style>
.theme-app { position: relative; z-index: 1; min-height: 100dvh; }
.ws-theme-root--desktop { --desktop-window-bar-height: 32px; --app-viewport-height: calc(100dvh - var(--desktop-window-bar-height)); }
.ws-theme-root--desktop .theme-app { padding-top: var(--desktop-window-bar-height); }
.ws-theme-root--desktop-auth { --desktop-window-bar-height: 0px; }
.ws-theme-root--storefront {
  min-height: 100dvh;
  color-scheme: light;
  --aw-canvas: #f6f7fb;
  --aw-surface: #ffffff;
  --aw-surface-subtle: #eef2f7;
  --aw-surface-strong: #e4eaf1;
  --aw-text: #172c4b;
  --aw-text-secondary: #647590;
  --aw-text-tertiary: #647590;
  --aw-border: #e5eaf3;
  --aw-border-strong: #8b98aa;
  --aw-primary: #6857ed;
  --aw-primary-hover: #5b49e0;
  --aw-primary-soft: #efecff;
  --aw-on-primary: #ffffff;
  --aw-on-graphite: #ffffff;
  --aw-info: #146c94;
  --aw-success: #16794c;
  --aw-warning: #9a6100;
  --aw-danger: #bd3636;
  --aw-graphite: #172033;
  --aw-graphite-strong: #101827;
  --aw-radius-xs: 4px;
  --aw-radius-sm: 6px;
  --aw-radius-md: 8px;
  --aw-radius-lg: 12px;
  --aw-shadow-panel: 0 8px 22px rgb(23 32 51 / 7%);
  --aw-shadow-float: 0 16px 40px rgb(23 32 51 / 14%);
  --ws-color-primary: var(--aw-primary);
  --ws-color-primary-hover: var(--aw-primary-hover);
  --ws-color-primary-soft: var(--aw-primary-soft);
  --ws-color-on-primary: var(--aw-on-primary);
  --ws-color-canvas: var(--aw-canvas);
  --ws-color-surface: var(--aw-surface);
  --ws-color-elevated: var(--aw-surface);
  --ws-color-surface-muted: var(--aw-surface-subtle);
  --ws-color-text: var(--aw-text);
  --ws-color-text-muted: var(--aw-text-secondary);
  --ws-color-text-subtle: var(--aw-text-tertiary);
  --ws-color-border: var(--aw-border);
  --ws-color-border-strong: var(--aw-border-strong);
  --ws-color-info: var(--aw-info);
  --ws-color-success: var(--aw-success);
  --ws-color-warning: var(--aw-warning);
  --ws-color-danger: var(--aw-danger);
  --ws-color-focus: var(--aw-primary);
  --ws-surface-shell-tint: var(--aw-canvas);
  --ws-surface-shell-opacity: 1;
  --ws-surface-control-tint: var(--aw-surface);
  --ws-surface-control-opacity: 1;
  --ws-surface-control-blur: 0px;
  --ws-surface-control-saturation: 100%;
  background: var(--aw-canvas);
}
.ws-theme-root--storefront .theme-app {
  background: var(--aw-canvas);
}
</style>
